import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Upload, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const RELIGIONS = ["", "Hindu", "Muslim", "Christian", "Sikh", "Jain", "Buddhist", "Other"];
const MARITAL = ["", "Never married", "Divorced", "Widowed", "Awaiting divorce"];
const DIET = ["", "Vegetarian", "Non-vegetarian", "Vegan", "Eggetarian", "Jain"];

export default function MyProfile() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const onboarding = params.get("onboarding") === "1";
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user) setForm({ ...user });
  }, [user]);

  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ["height_cm", "partner_age_min", "partner_age_max"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = parseInt(payload[k], 10);
      });
      delete payload.id; delete payload.email; delete payload.role; delete payload.photos; delete payload.age; delete payload.profile_complete;
      await api.put("/profile/me", payload);
      await refresh();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = async (path) => {
    try {
      await api.delete("/photos", { params: { path } });
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="my-profile-page">
      <p className="overline mb-3">{onboarding ? "Welcome" : "My profile"}</p>
      <h1 className="font-serif-display text-4xl md:text-5xl mb-3 tracking-tight">
        {onboarding ? "Tell us about yourself." : "Edit your profile."}
      </h1>
      <p className="text-sm text-[#8C857B] mb-10 max-w-xl">
        These details help others know you better. A complete profile appears in discovery.
      </p>

      {/* Verification CTA */}
      {!user.verified && (
        <div className="mb-10 p-5 rounded-2xl bg-[#F2EFE9] border border-[#E2DCD0] flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="verify-cta">
          <div>
            <p className="font-serif-display text-xl">Get a Verified badge</p>
            <p className="text-xs text-[#8C857B] mt-1">Verified profiles earn 4x more responses. Request a review from our team.</p>
          </div>
          <button data-testid="verify-request-btn" onClick={async () => {
            try { await api.post("/users/me/request-verification"); toast.success("Request submitted. We'll review soon."); }
            catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
          }} className="rounded-full bg-[#2C2A28] hover:bg-black text-white px-5 py-2.5 text-sm transition">
            Request verification
          </button>
        </div>
      )}

      {/* Photos */}
      <div className="mb-12">
        <p className="overline mb-4">Photos</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {(user.photos || []).map((p) => (
            <div key={p} className="relative aspect-square rounded-2xl overflow-hidden bg-[#F2EFE9] group" data-testid={`photo-${p}`}>
              <img src={fileUrl(p)} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <button onClick={() => removePhoto(p)} data-testid="photo-delete-btn"
                className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <label className="aspect-square rounded-2xl border-2 border-dashed border-[#E2DCD0] hover:border-[#C85A44] hover:bg-[#F2EFE9] cursor-pointer flex flex-col items-center justify-center text-[#8C857B] hover:text-[#C85A44] transition" data-testid="photo-upload-label">
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid="photo-upload-input" />
            {uploading ? <Upload className="h-6 w-6 animate-pulse" /> : <ImagePlus className="h-6 w-6" strokeWidth={1.5} />}
            <span className="text-xs mt-2">{uploading ? "Uploading…" : "Add photo"}</span>
          </label>
        </div>
      </div>

      <form onSubmit={save} className="space-y-10">
        <Section title="Basics">
          <Field label="Full name"><input value={form.full_name || ""} onChange={upd("full_name")} className={inputCls} data-testid="pf-full-name" /></Field>
          <Field label="Date of birth"><input type="date" value={form.date_of_birth?.slice(0,10) || ""} onChange={upd("date_of_birth")} className={inputCls} data-testid="pf-dob" /></Field>
          <Field label="Height (cm)"><input type="number" min={120} max={220} value={form.height_cm || ""} onChange={upd("height_cm")} className={inputCls} data-testid="pf-height" /></Field>
          <Field label="Marital status"><select value={form.marital_status || ""} onChange={upd("marital_status")} className={inputCls} data-testid="pf-marital">
            {MARITAL.map((m) => <option key={m} value={m}>{m || "Select…"}</option>)}
          </select></Field>
          <Field label="Birth time (optional)"><input type="time" value={form.birth_time || ""} onChange={upd("birth_time")} className={inputCls} data-testid="pf-btime" /></Field>
          <Field label="Birth place (optional)"><input value={form.birth_place || ""} onChange={upd("birth_place")} className={inputCls} data-testid="pf-bplace" /></Field>
        </Section>

        <Section title="Background">
          <Field label="Religion"><select value={form.religion || ""} onChange={upd("religion")} className={inputCls} data-testid="pf-religion">
            {RELIGIONS.map((r) => <option key={r} value={r}>{r || "Select…"}</option>)}
          </select></Field>
          <Field label="Community / Caste"><input value={form.community || ""} onChange={upd("community")} className={inputCls} data-testid="pf-community" /></Field>
          <Field label="Mother tongue"><input value={form.mother_tongue || ""} onChange={upd("mother_tongue")} className={inputCls} data-testid="pf-mtongue" /></Field>
          <Field label="Diet"><select value={form.diet || ""} onChange={upd("diet")} className={inputCls} data-testid="pf-diet">
            {DIET.map((d) => <option key={d} value={d}>{d || "Select…"}</option>)}
          </select></Field>
        </Section>

        <Section title="Location & career">
          <Field label="City"><input value={form.city || ""} onChange={upd("city")} className={inputCls} data-testid="pf-city" /></Field>
          <Field label="Country"><input value={form.country || ""} onChange={upd("country")} className={inputCls} data-testid="pf-country" /></Field>
          <Field label="Education"><input value={form.education || ""} onChange={upd("education")} className={inputCls} data-testid="pf-education" /></Field>
          <Field label="Profession"><input value={form.profession || ""} onChange={upd("profession")} className={inputCls} data-testid="pf-profession" /></Field>
          <Field label="Annual income"><input value={form.annual_income || ""} onChange={upd("annual_income")} className={inputCls} data-testid="pf-income" /></Field>
        </Section>

        <Section title="Story" full>
          <Field label="About me" full>
            <textarea rows={5} value={form.about_me || ""} onChange={upd("about_me")} className={inputCls + " resize-none"} data-testid="pf-about" />
          </Field>
          <Field label="About my family" full>
            <textarea rows={4} value={form.family_about || ""} onChange={upd("family_about")} className={inputCls + " resize-none"} data-testid="pf-family" />
          </Field>
        </Section>

        <Section title="Partner preferences">
          <Field label="Partner age (min)"><input type="number" min={18} max={80} value={form.partner_age_min || ""} onChange={upd("partner_age_min")} className={inputCls} data-testid="pf-pamin" /></Field>
          <Field label="Partner age (max)"><input type="number" min={18} max={80} value={form.partner_age_max || ""} onChange={upd("partner_age_max")} className={inputCls} data-testid="pf-pamax" /></Field>
          <Field label="Partner religion"><select value={form.partner_religion || ""} onChange={upd("partner_religion")} className={inputCls} data-testid="pf-preligion">
            {RELIGIONS.map((r) => <option key={r} value={r}>{r || "Any"}</option>)}
          </select></Field>
          <Field label="About preferred partner" full>
            <textarea rows={3} value={form.partner_about || ""} onChange={upd("partner_about")} className={inputCls + " resize-none"} data-testid="pf-pabout" />
          </Field>
        </Section>

        <div className="flex justify-end pt-4">
          <button type="submit" disabled={saving} data-testid="profile-save-button"
            className="rounded-full bg-[#C85A44] hover:bg-[#A84A36] disabled:opacity-60 text-white px-8 py-3 text-sm transition">
            {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-[#E2DCD0] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A44]/30 focus:border-[#C85A44] transition";

function Section({ title, children, full }) {
  return (
    <div>
      <p className="overline mb-5">{title}</p>
      <div className={`grid ${full ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"} gap-5`}>{children}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-sm mb-2 text-[#5d574f]">{label}</label>
      {children}
    </div>
  );
}
